import React, { memo } from "react";
import EmailAddress from "../../types/core/EmailAddress";
import PhoneNumber from "../../types/core/PhoneNumber";
import User from "../../types/core/User";
import UserDigest from "../../types/core/UserDigest";
import PageSpecification from "../../types/system/PageSpecification";
import GridColumn, { GridColumnType } from "../../components/grid/GridColumn";
import GridEditor from "../../components/grid/GridEditor";
import Formatting from "../../foundation/Formatting";
import Address from "../../types/core/Address";
import SchoolAddress from "../../types/core/SchoolAddress";
import Utility from "../../foundation/Utility";

interface GridCellProps<T> {
    column: GridColumn<T>;
    record: T;
    value?: any;

    draggable?: boolean;
    selected: boolean;
    globalSelect: boolean;
    singleSelected: boolean;
    pageSpecification?: PageSpecification;
    icon?: string;

    onKeyDown?: (event: KeyboardEvent) => void;
    onKeyUp?: (event: KeyboardEvent) => void;
    onFocus?: (record: T, column: GridColumn<T>) => void;
    onBlur?: (record: T, column: GridColumn<T>) => void;
    onClick?: (record: T, column: GridColumn<T>) => void;
    onDoubleClick?: (record: T, column: GridColumn<T>) => void;
    onDragStart?: (
        record: T,
        column: GridColumn<T>,
        event: React.MouseEvent,
        cell: React.RefObject<HTMLTableCellElement>,
    ) => void;
    valueEdited?: (record: T, column: GridColumn<T>, value: any) => void;
    prevRecordRequested?: () => void;
    nextRecordRequested?: () => void;
    prevColumnRequested?: () => void;
    nextColumnRequested?: () => void;
}

interface GridCellState {
    cellRef: React.RefObject<HTMLTableCellElement>;
}

class GridCell<T> extends React.Component<GridCellProps<T>, GridCellState> {
    state = {
        cellRef: React.createRef<HTMLTableCellElement>(),
    } as GridCellState;

    componentDidUpdate(
        prevProps: Readonly<GridCellProps<T>>,
        prevState: Readonly<GridCellState>,
        snapshot?: any,
    ): void {
        if (!prevProps.selected && this.props.selected && !this.editing()) {
            this.state.cellRef?.current?.focus();
        }
    }

    numeric() {
        return (
            this.props.column.type === GridColumnType.Number ||
            (typeof this.props.value === "number" &&
                this.props.column.type !== GridColumnType.Dropdown)
        );
    }
    selectable() {
        return (
            (this.props.globalSelect ||
                this.props.column.selectable ||
                this.props.column.editable) &&
            typeof this.props.value !== "boolean"
        );
    }
    editable() {
        return this.props.column.editableField
            ? (this.props.record[this.props.column.editableField] as boolean)
            : this.props.column.editable;
    }
    editing() {
        return this.props.selected && this.editable();
    }
    fieldEditable() {
        return this.props.column.editableField
            ? (this.props.record[this.props.column.editableField] as boolean)
            : this.props.column.editable;
    }
    tooltip() {
        return this.props.column.cellTooltip
            ? this.props.column.cellTooltip
            : this.props.column.cellTooltipField
            ? this.props.record[this.props.column.cellTooltipField]
            : this.props.column.cellTooltipMethod
            ? this.props.column.cellTooltipMethod(this.props.record)
            : null;
    }

    keyDown(event: KeyboardEvent) {
        if (this.props.onKeyDown) this.props.onKeyDown(event);
        if (
            ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(
                event.key,
            )
        )
            event.preventDefault();
    }
    keyUp(event: KeyboardEvent) {
        if (this.props.onKeyUp) this.props.onKeyUp(event);
        switch (event.key) {
            case "ArrowUp":
                this.props.prevRecordRequested();
                break;
            case "Enter":
            case "ArrowDown":
                this.props.nextRecordRequested();
                break;
            case "ArrowLeft":
                this.props.prevColumnRequested();
                break;
            case "ArrowRight":
                this.props.nextColumnRequested();
                break;
        }
    }

    formatValue(value: any) {
        switch (this.props.column.type) {
            case GridColumnType.Dropdown:
                return this.props.column.options?.find(o => o.value === value)
                    ?.text;
            case GridColumnType.Number:
                return Formatting.formatNumber(
                    value,
                    this.props.column.decimalPlaces,
                    this.props.column.naForNull,
                );
            case GridColumnType.Checkbox:
                return (
                    <span
                        className={String(value)}
                        tabIndex={0}
                        onClickCapture={this.checkClick.bind(this)}
                    />
                );
            case GridColumnType.Html:
                return (
                    <span dangerouslySetInnerHTML={{ __html: String(value) }} />
                );
            default:
                if (Utility.isNull(value)) return "";
        }

        switch (value.constructor) {
            case User:
                return (value as User).fullName ?? "Unknown User";
            case UserDigest:
                return (value as UserDigest).fullName ?? "Unknown User";
            case Date:
                if (
                    this.props.column.includeDate !== false &&
                    this.props.column.includeTime === true
                )
                    return Formatting.formatDateTime(value);
                else if (this.props.column.includeDate !== false)
                    return Formatting.formatDate(value);
                else if (this.props.column.includeTime === true)
                    return Formatting.formatTime(value);
                else return "";
            case Address:
                return Formatting.formatAddress(value);
            case SchoolAddress:
                return Formatting.formatSchoolAddress(value);
            case PhoneNumber:
                return Formatting.formatPhoneNumber(value);
            case EmailAddress:
                return Formatting.formatEmailAddress(value);
            default:
                return String(value);
        }
    }

    checkClick() {
        if (this.props.column.type === GridColumnType.Checkbox) {
            this.props.valueEdited(
                this.props.record,
                this.props.column,
                !(this.props.value as boolean),
            );
        }
        this.props.onClick?.(this.props.record, this.props.column);
    }

    render(): React.ReactNode {
        let classes = [];

        if (this.props.column.type) {
            classes.push(GridColumnType[this.props.column.type].toLowerCase());
        } else if (this.props.value) {
            classes.push(typeof this.props.value);
        }

        switch (this.props.column.alignment) {
            case "left":
                classes.push("text-left");
                break;
            case "center":
                classes.push("text-center");
                break;
            case "right":
                classes.push("text-right");
                break;
        }

        if (this.props.selected) classes.push("selected");
        if (this.props.singleSelected) classes.push("single-selected");
        if (this.selectable()) classes.push("selectable");
        if (this.editable()) classes.push("editable");
        if (this.numeric()) classes.push("numeric");

        return (
            <td
                ref={this.state.cellRef}
                className={classes.join(" ")}
                style={{ width: this.props.column.width }}
                tabIndex={this.selectable() && !this.editing() ? 0 : null}
                draggable={this.props.draggable}
                onFocus={() =>
                    this.selectable() &&
                    this.props.onFocus(this.props.record, this.props.column)
                }
                onBlur={() =>
                    this.props.onBlur(this.props.record, this.props.column)
                }
                onClick={ev =>
                    this.selectable() &&
                    this.props.onClick?.(this.props.record, this.props.column)
                }
                onDoubleClick={() =>
                    this.selectable() &&
                    this.props.onDoubleClick(
                        this.props.record,
                        this.props.column,
                    )
                }
                onDragStart={event =>
                    this.selectable() &&
                    event.preventDefault() === undefined &&
                    this.props.onDragStart(
                        this.props.record,
                        this.props.column,
                        event,
                        this.state.cellRef,
                    )
                }
                onKeyDown={this.keyDown.bind(this)}
                onKeyUp={this.keyUp.bind(this)}
                data-tip={this.tooltip()}
                data-place="left"
                data-html={this.props.column.cellTooltipHtml}>
                {this.editing() && (
                    <GridEditor
                        type={this.props.column.type}
                        icon={this.props.column.icon}
                        value={this.props.value}
                        blank={this.props.column.blank}
                        allowNull={this.props.column.allowNull}
                        naForNull={this.props.column.naForNull}
                        decimalPlaces={this.props.column.decimalPlaces}
                        decimalIncrement={this.props.column.decimalIncrement}
                        options={this.props.column.options}
                        editable={this.fieldEditable()}
                        onClick={() =>
                            this.props.column?.onClick?.(
                                this.props.record,
                                this.props.column,
                            )
                        }
                        valueEdited={value =>
                            this.editing() &&
                            this.props.valueEdited(
                                this.props.record,
                                this.props.column,
                                value,
                            )
                        }
                        prevRecordRequested={this.props.prevRecordRequested}
                        nextRecordRequested={this.props.nextRecordRequested}
                        prevColumnRequested={this.props.prevColumnRequested}
                        nextColumnRequested={this.props.nextColumnRequested}
                    />
                )}
                {!this.editing() &&
                    (this.props.column.icon ? (
                        <i>{this.props.column.icon}</i>
                    ) : !this.props.pageSpecification?.waiting ? (
                        this.formatValue(this.props.value)
                    ) : null)}
            </td>
        );
    }
}

export default memo(GridCell);
